import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';

import { Label } from '@/components/ui/label';
import config from 'constants/apiConfig';
import {
  HwcgEocAssetCard,
  type HwcgEocAssetSummary,
} from 'components/IncidentConfig/CreateIncident/HwcgEocAssetCard';
import { HwcgEocActionPanel } from 'components/IncidentConfig/CreateIncident/HwcgEocActionPanel';
import { HwcgGroupActionsEditor } from 'components/IncidentConfig/CreateIncident/HwcgGroupActionsEditor';
import {
  createEmptyHwcgEocNotice,
  hwcgEocCompanyMatches,
  hwcgGroupNoticePayload,
  type HwcgEocNoticeState,
} from 'components/IncidentConfig/CreateIncident/hwcgEocNotice';
import {
  createDefaultHwcgIncidentSpecifics,
  HWCG_COMPANY_OPTIONS,
  type HwcgIncidentSpecificsState,
} from 'components/IncidentConfig/CreateIncident/hwcgIncidentSpecifics';
import {
  createEmptyHwcgGroupActions,
  HWCG_GROUP_LABELS,
  type HwcgContactListId,
  type HwcgGroupActionsState,
  type HwcgGroupId,
} from 'components/IncidentConfig/CreateIncident/hwcgGroupActions';
import {
  deliveryStateLabel,
  hwcgEocNoticeResponse,
  mergeHwcgContactNoticeStatus,
  parseWorkspaceEocOutcome,
  workspaceEocOutcomeSummary,
} from 'components/IncidentConfig/CreateIncident/hwcgNoticeContactStatus';
import { SearchableSelect } from 'components/IncidentConfig/roster/SearchableSelect';
import { useHwcgContactLists } from 'hooks/hwcgContactLists/hwcgContactListHooks';
import {
  eocLocationForCompany,
  officeLocationForCompany,
  useHwcgMemberCompanies,
} from 'hooks/hwcgMemberCompanies/hwcgMemberCompanyHooks';
import { useHwcgNoticeStepReports } from 'hooks/useHwcgNoticeStepReport';
import {
  hwcgActivationLogKeys,
  useHwcgActivationLog,
} from 'hooks/useHwcgActivationLog';
import { HwcgGroupNoticeLog } from 'components/IncidentConfig/roster/activationLog/HwcgGroupNoticeLog';
import {
  groupNoticeLogRows,
  mergeLatestNoticeContacts,
} from 'components/IncidentConfig/roster/activationLog/hwcgActivationLogModel';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import { useHwcgFlag } from 'utils/featureFlags';

const COMPANY_OPTIONS = HWCG_COMPANY_OPTIONS.map((company) => ({
  value: company,
  label: company,
}));

type GroupActionsResponse = {
  incidentName: string;
  actions: HwcgGroupActionsState | null;
  responsibleParty: string;
  specifics: Partial<HwcgIncidentSpecificsState>;
  eoc: unknown;
  notices: Record<string, unknown>;
  groupNoticeNotificationIds?: Record<string, string>;
  groupNoticeContactNotificationIds?: Record<string, Record<string, string>>;
  eocOutcome?: unknown;
  eocAsset?: HwcgEocAssetSummary | null;
};

const keys = {
  one: (incidentId: string) => ['hwcg-group-actions', incidentId] as const,
};

function asNotice(value: unknown): HwcgEocNoticeState {
  const empty = createEmptyHwcgEocNotice();
  if (!value || typeof value !== 'object' || Array.isArray(value)) return empty;
  const row = value as Partial<HwcgEocNoticeState>;
  return {
    ...empty,
    ...row,
    recipientContactIds: Array.isArray(row.recipientContactIds)
      ? row.recipientContactIds.filter((id) => typeof id === 'string')
      : [],
    channels:
      Array.isArray(row.channels) && row.channels.length > 0
        ? row.channels
        : empty.channels,
  };
}

function addTargetFor(groupId: HwcgGroupId, companyName: string) {
  if (groupId === 'eoc') {
    return {
      constituency: 'eoc' as HwcgContactListId,
      memberCompany: companyName.trim(),
    };
  }
  if (groupId === 'responsible_party') {
    return {
      constituency: 'mutual_aid' as HwcgContactListId,
      memberCompany: companyName.trim(),
    };
  }
  if (groupId === 'service_provider') {
    return {
      constituency: 'service_provider' as HwcgContactListId,
      memberCompany: '',
    };
  }
  return { constituency: 'mutual_aid' as HwcgContactListId, memberCompany: '' };
}

export default function IncidentGroupActionsPage() {
  const hwcg = useHwcgFlag();
  const { incidentId = '' } = useParams<{ incidentId: string }>();
  const groupGuid = useAppSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  const activationLog = useHwcgActivationLog(incidentId);
  const query = useQuery({
    queryKey: [...keys.one(incidentId), groupGuid],
    enabled: Boolean(incidentId),
    queryFn: async () => {
      const response = await axios.get<GroupActionsResponse>(
        config.apiGateway.hwcgIncidentGroupActions(incidentId),
        { params: groupGuid ? { groupGuid } : undefined }
      );
      return response.data;
    },
  });
  const [actions, setActions] = useState<HwcgGroupActionsState>(
    createEmptyHwcgGroupActions()
  );
  const [eoc, setEoc] = useState<HwcgEocNoticeState>(
    createEmptyHwcgEocNotice()
  );
  const [notices, setNotices] = useState<Record<string, HwcgEocNoticeState>>(
    {}
  );
  const [specifics, setSpecifics] = useState<HwcgIncidentSpecificsState>(
    createDefaultHwcgIncidentSpecifics()
  );
  const [selected, setSelected] = useState<HwcgGroupId>('eoc');
  const [responsibleParty, setResponsibleParty] = useState('');
  const [contactNoticeIds, setContactNoticeIds] = useState<
    Record<string, Record<string, string>>
  >({});
  const hydratedRef = useRef(false);
  const skipAutosaveRef = useRef(true);

  useEffect(() => {
    if (!query.data) return;
    const loadedSpecifics = {
      ...createDefaultHwcgIncidentSpecifics(),
      ...query.data.specifics,
      companyName:
        query.data.specifics?.companyName || query.data.responsibleParty || '',
    };
    setActions(query.data.actions ?? createEmptyHwcgGroupActions());
    setEoc(asNotice(query.data.eoc));
    setNotices(
      Object.fromEntries(
        Object.entries(query.data.notices ?? {}).map(([id, notice]) => [
          id,
          asNotice(notice),
        ])
      )
    );
    setSpecifics(loadedSpecifics);
    setResponsibleParty(
      query.data.responsibleParty || loadedSpecifics.companyName
    );
    setContactNoticeIds(query.data.groupNoticeContactNotificationIds ?? {});
    hydratedRef.current = true;
    skipAutosaveRef.current = true;
  }, [query.data]);

  const contactLists = useHwcgContactLists({ enabled: hwcg, groupGuid });
  const memberCompanies = useHwcgMemberCompanies({ enabled: hwcg, groupGuid });
  const contacts = (contactLists.data ?? []).flatMap((list) =>
    list.members.map((member) => ({
      ...member,
      constituency: (list.constituency === 'responsible_party'
        ? 'mutual_aid'
        : list.constituency) as HwcgContactListId,
    }))
  );
  const mutualAidOthers = contacts.filter(
    (contact) =>
      contact.constituency === 'mutual_aid' &&
      !hwcgEocCompanyMatches(contact.memberCompany, responsibleParty)
  );
  const serviceProviderContacts = contacts.filter(
    (contact) => contact.constituency === 'service_provider'
  );
  const notice =
    selected === 'eoc'
      ? eoc
      : (notices[selected] ?? createEmptyHwcgEocNotice());
  const selectedAction = actions[selected];
  const canSend = selectedAction === 'activate' || selectedAction === 'notify';

  const payload = useMemo(
    () => ({
      groupGuid,
      actions,
      responsibleParty,
      eoc,
      notices,
    }),
    [groupGuid, actions, responsibleParty, eoc, notices]
  );

  const save = useMutation({
    mutationFn: async () => {
      await axios.put(
        config.apiGateway.hwcgIncidentGroupActions(incidentId),
        payload
      );
    },
    onError: () => toast.error('Could not save group actions'),
  });

  useEffect(() => {
    if (!hydratedRef.current || !incidentId || !groupGuid) return;
    if (skipAutosaveRef.current) {
      skipAutosaveRef.current = false;
      return;
    }
    const timer = window.setTimeout(() => {
      save.mutate();
    }, 600);
    return () => window.clearTimeout(timer);
    // Intentionally depend on payload fields, not the mutation object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload, incidentId, groupGuid]);

  const send = useMutation({
    mutationFn: async (input: {
      contactIds: string[];
      noticeScript?: string;
      channels?: HwcgEocNoticeState['channels'];
    }) => {
      const action = selectedAction === 'notify' ? 'notify' : 'activate';
      const baseNotice = hwcgGroupNoticePayload(
        selected,
        action,
        notice,
        specifics
      );
      const response = await axios.post<{ notificationId: string | null }>(
        `${config.apiGateway.hwcgIncidentGroupActions(incidentId)}/send`,
        {
          ...payload,
          groupId: selected,
          notice: {
            ...baseNotice,
            recipientContactIds: input.contactIds,
            ...(input.noticeScript ? { noticeScript: input.noticeScript } : {}),
            ...(input.channels ? { channels: input.channels } : {}),
          },
        }
      );
      return {
        notificationId: response.data.notificationId,
        contactIds: input.contactIds,
      };
    },
    onSuccess: ({ notificationId, contactIds }) => {
      if (!notificationId) {
        toast.warn('Choose at least one recipient before sending.');
        return;
      }
      setContactNoticeIds((current) => {
        const groupMap = { ...(current[selected] ?? {}) };
        for (const contactId of contactIds) {
          groupMap[contactId] = notificationId;
        }
        return { ...current, [selected]: groupMap };
      });
      queryClient.setQueryData<GroupActionsResponse>(
        keys.one(incidentId),
        (current) =>
          current
            ? {
                ...current,
                groupNoticeNotificationIds: {
                  ...(current.groupNoticeNotificationIds ?? {}),
                  [selected]: notificationId,
                },
                groupNoticeContactNotificationIds: {
                  ...(current.groupNoticeContactNotificationIds ?? {}),
                  [selected]: {
                    ...(current.groupNoticeContactNotificationIds?.[selected] ??
                      {}),
                    ...Object.fromEntries(
                      contactIds.map((id) => [id, notificationId])
                    ),
                  },
                },
              }
            : current
      );
      toast.success(
        contactIds.length === 1
          ? 'Notice sent to recipient'
          : `Notice sent to ${contactIds.length} recipients`
      );
      void queryClient.invalidateQueries({ queryKey: keys.one(incidentId) });
      void queryClient.invalidateQueries({
        queryKey: ['hwcgNoticeStepReport'],
      });
      void queryClient.invalidateQueries({
        queryKey: hwcgActivationLogKeys.byIncident(incidentId),
      });
    },
    onError: () => toast.error('Could not send the group action'),
  });

  const resend = useMutation({
    mutationFn: async (input: {
      notificationId: string;
      userGuids: string[];
    }) => {
      await axios.post(
        `${config.apiGateway.hwcgIncidentGroupActions(incidentId)}/resend`,
        input
      );
    },
    onSuccess: () => {
      toast.success('Notification resent');
      void queryClient.invalidateQueries({
        queryKey: hwcgActivationLogKeys.byIncident(incidentId),
      });
      void queryClient.invalidateQueries({
        queryKey: ['hwcgNoticeStepReport'],
      });
    },
    onError: () => toast.error('Could not resend the notification'),
  });

  const selectedContactMap = contactNoticeIds[selected] ?? {};
  const notificationIds = useMemo(
    () => Object.values(selectedContactMap),
    [selectedContactMap]
  );
  const stepReports = useHwcgNoticeStepReports(notificationIds, {
    enabled: hwcg && canSend,
  });

  const panelContacts =
    selected === 'mutual_aid'
      ? mutualAidOthers
      : selected === 'service_provider'
        ? serviceProviderContacts
        : contacts;

  const contactStatusById = useMemo(
    () =>
      mergeHwcgContactNoticeStatus({
        contacts: panelContacts,
        contactNotificationIds: selectedContactMap,
        reportsByNotificationId: stepReports.reportsByNotificationId,
      }),
    [panelContacts, selectedContactMap, stepReports.reportsByNotificationId]
  );

  const groupLogRows = useMemo(() => {
    const action = selectedAction === 'notify' ? 'notify' : undefined;
    const fromLog = groupNoticeLogRows(
      activationLog.data?.users ?? [],
      selected,
      action
    );
    const loggedIds = new Set(fromLog.map((row) => row.notificationId));
    const overlayAction = selectedAction === 'notify' ? 'notify' : 'activate';
    return mergeLatestNoticeContacts(
      fromLog,
      panelContacts.flatMap((contact) => {
        const status = contactStatusById[contact.id];
        const notificationId = status?.notificationId ?? null;
        if (
          selectedAction === 'notify' &&
          (!notificationId || !loggedIds.has(notificationId))
        ) {
          return [];
        }
        const name = [contact.firstName, contact.lastName]
          .filter(Boolean)
          .join(' ')
          .trim();
        return [
          {
            id: contact.id,
            userGuid: contact.userGuid,
            name: name || contact.name || contact.email || 'Recipient',
            notificationId,
            sentAt: status?.sentAt ?? null,
            delivery: deliveryStateLabel(status?.deliveryState),
            acknowledged: hwcgEocNoticeResponse(status),
            action: overlayAction,
          },
        ];
      })
    );
  }, [
    activationLog.data?.users,
    contactStatusById,
    panelContacts,
    selected,
    selectedAction,
  ]);

  const savedEocOutcome = parseWorkspaceEocOutcome(query.data?.eocOutcome);
  const eocOutcomeSummary = workspaceEocOutcomeSummary(savedEocOutcome);

  const setNotice = (next: HwcgEocNoticeState) => {
    if (selected === 'eoc') {
      setEoc(next);
      return;
    }
    setNotices((current) => ({ ...current, [selected]: next }));
  };

  const setCompany = (companyName: string) => {
    setResponsibleParty(companyName);
    setSpecifics((current) => ({ ...current, companyName }));
    const eocLocation = eocLocationForCompany(
      memberCompanies.data ?? [],
      companyName
    );
    if (eocLocation) {
      setEoc((current) => ({
        ...current,
        locationLabel: eocLocation,
        location: null,
      }));
    }
    const officeLocation = officeLocationForCompany(
      memberCompanies.data ?? [],
      companyName
    );
    if (officeLocation) {
      setNotices((current) => ({
        ...current,
        responsible_party: {
          ...(current.responsible_party ?? createEmptyHwcgEocNotice()),
          locationLabel: officeLocation,
          location: null,
        },
      }));
    }
  };

  // Fill EOC + report-to from the directory when RP is set and fields are empty.
  useEffect(() => {
    if (!hwcg || !memberCompanies.data?.length) return;
    if (!eoc.locationLabel.trim()) {
      const location = eocLocationForCompany(
        memberCompanies.data,
        responsibleParty
      );
      if (location) {
        setEoc((current) => ({
          ...current,
          locationLabel: location,
          location: null,
        }));
      }
    }
    const rpNotice = notices.responsible_party;
    if (!rpNotice?.locationLabel?.trim()) {
      const office = officeLocationForCompany(
        memberCompanies.data,
        responsibleParty
      );
      if (office) {
        setNotices((current) => ({
          ...current,
          responsible_party: {
            ...(current.responsible_party ?? createEmptyHwcgEocNotice()),
            locationLabel: office,
            location: null,
          },
        }));
      }
    }
  }, [
    hwcg,
    memberCompanies.data,
    responsibleParty,
    eoc.locationLabel,
    notices.responsible_party,
  ]);

  if (!hwcg) return null;

  return (
    <div className="maincontent dark tw:text-foreground">
      <div className="tw:mx-auto tw:flex tw:max-w-7xl tw:flex-col tw:gap-4 tw:p-4">
        <div>
          <h1 className="tw:m-0 tw:text-lg tw:font-semibold">Group Actions</h1>
          <p className="tw:mt-1 tw:text-sm tw:text-muted-foreground">
            Choices update automatically as you edit. Send activate or notify
            notices to selected recipients below.
          </p>
        </div>
        <div className="tw:grid tw:max-w-xl tw:gap-1.5">
          <Label htmlFor="hwcg-ga-responsible-party">Responsible Party</Label>
          <SearchableSelect
            id="hwcg-ga-responsible-party"
            aria-label="Responsible Party"
            options={COMPANY_OPTIONS}
            value={
              responsibleParty
                ? { value: responsibleParty, label: responsibleParty }
                : null
            }
            placeholder="Select responsible party"
            contentClassName="dark"
            positionerClassName="tw:z-[60]"
            onChange={(next) => setCompany(next?.value ?? '')}
          />
          {!responsibleParty.trim() ? (
            <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
              Select a responsible party to list EOC / Responsible Party
              recipients and enable + Add.
            </p>
          ) : null}
        </div>
        <HwcgGroupActionsEditor
          actions={actions}
          onChange={setActions}
          selectedId={selected}
          onSelect={setSelected}
          eocOutcomePanel={
            query.data?.eocAsset ? (
              <HwcgEocAssetCard
                asset={query.data.eocAsset}
                incidentId={incidentId}
              />
            ) : null
          }
          noticePanel={
            canSend ? (
              <HwcgEocActionPanel
                groupId={selected}
                groupLabel={HWCG_GROUP_LABELS[selected]}
                action={selectedAction === 'notify' ? 'notify' : 'activate'}
                companyName={responsibleParty}
                specifics={specifics}
                notice={notice}
                contacts={panelContacts}
                matchCompany={
                  selected === 'eoc' || selected === 'responsible_party'
                }
                addTarget={addTargetFor(selected, responsibleParty)}
                onChange={setNotice}
                sendMode
                incidentId={incidentId}
                sendPending={send.isPending}
                onSend={(contactIds, options) =>
                  send.mutate({
                    contactIds,
                    noticeScript: options?.noticeScript,
                    channels: options?.channels,
                  })
                }
                contactStatusById={contactStatusById}
              />
            ) : null
          }
        />
        <section
          className="tw:flex tw:flex-col tw:gap-4 tw:rounded-md tw:border tw:border-border tw:p-4"
          aria-labelledby="hwcg-group-log-heading"
        >
          <div>
            <h2
              id="hwcg-group-log-heading"
              className="tw:m-0 tw:text-base tw:font-semibold"
            >
              Log
            </h2>
            <p className="tw:mt-1 tw:text-sm tw:text-muted-foreground">
              {selectedAction === 'notify'
                ? `Every notification sent from ${HWCG_GROUP_LABELS[selected]}.`
                : `Notices sent to ${HWCG_GROUP_LABELS[selected]}.`}
            </p>
            {selected === 'eoc' ? (
              <p className="tw:mt-2 tw:text-sm">
                <span className="tw:font-medium">
                  {eocOutcomeSummary.label}
                </span>
                {eocOutcomeSummary.note ? (
                  <span className="tw:mt-1 tw:block tw:whitespace-pre-wrap tw:text-muted-foreground">
                    {eocOutcomeSummary.note}
                  </span>
                ) : null}
              </p>
            ) : null}
          </div>
          <HwcgGroupNoticeLog
            rows={groupLogRows}
            isLoading={activationLog.isLoading}
            isError={activationLog.isError}
            resendPending={resend.isPending}
            onResend={(notificationId, userGuids) =>
              resend.mutate({ notificationId, userGuids })
            }
          />
        </section>
      </div>
    </div>
  );
}
